import { useExtrasLabels } from '@features/spa/hooks';
import { useGetSpaPackageExtras } from '@features/spa/hooks/useGetSpaPackageExtras';
import { adaptExtras } from '@features/spa/pages/SpaDay/SpaPackageModal/SpaPackageExtras/utils';
import { useTranslations } from '@lang';
import moment from 'moment';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import type { ExtrasItem } from 'voyager';
import { useCurrentStep, usePackageConfiguration } from '../../contexts';
import { formatDateISO } from '../../helpers/dateFormatters';
import { fillMissingEntries, toSpaExtraEntries } from '../../helpers/extrasEntries';
import { compareGuests } from '../../helpers/guest.helpers';
import { PackagesFormValues } from '../../types';
import { PackageExtrasPage } from '../PackageExtrasPage';

export const SpaExtrasStepV2 = () => {
  const { t } = useTranslations();
  const { property, language, journeyParams, spaPackageId } = usePackageConfiguration();
  const { packageTotalPrice } = useCurrentStep();
  const { watch, setValue, getValues } = useFormContext<PackagesFormValues>();

  const { data: extras = [] } = useGetSpaPackageExtras(
    property.id,
    Number(spaPackageId),
    language,
    formatDateISO(moment(journeyParams.fromDate)),
    true
  );

  const adaptedExtras = useMemo(() => adaptExtras(extras), [extras]);
  const formGuests = watch('guests');
  const formSpaExtras = watch('spaExtras');
  const guestDetails = useMemo(
    () => (formGuests ?? []).map(({ guestDetails }) => guestDetails),
    [formGuests]
  );
  const entries = useMemo(() => formSpaExtras ?? [], [formSpaExtras]);
  const sharedLabels = useExtrasLabels();

  useEffect(() => {
    if (adaptedExtras.length && !(getValues('spaExtras') ?? []).length) {
      setValue('spaExtras', toSpaExtraEntries(adaptedExtras));
    }
  }, [adaptedExtras, getValues, setValue]);

  const items: ExtrasItem[] = useMemo(
    () =>
      adaptedExtras.map((extra, index) => {
        const held = entries[index]?.selectedGuestDetails ?? [];
        const selectedGuestIds = guestDetails.flatMap((guest, guestIndex) =>
          held.some((selected) => compareGuests(selected, guest)) ? [String(guestIndex)] : []
        );
        const selected = selectedGuestIds.length > 0;
        return {
          id: String(index),
          image: { src: extra.image?.url, alt: extra.name ?? '' },
          title: extra.name ?? '',
          price: {
            amount: extra.price?.amount ?? 0,
            currencyCode: extra.price?.currencyCode ?? packageTotalPrice.currencyCode,
          },
          selected,
          quantity: selected ? selectedGuestIds.length : undefined,
          selectedGuestIds: selected ? selectedGuestIds : undefined,
        };
      }),
    [adaptedExtras, entries, guestDetails, packageTotalPrice.currencyCode]
  );

  const setSelectedGuests = (itemId: string, selectedGuestIds: string[]) => {
    const updated = fillMissingEntries(
      toSpaExtraEntries(adaptedExtras),
      getValues('spaExtras') ?? []
    );
    const index = Number(itemId);
    updated[index] = {
      ...updated[index],
      selectedGuestDetails: selectedGuestIds
        .map((guestId) => guestDetails[Number(guestId)])
        .filter(Boolean),
    };
    setValue('spaExtras', updated, { shouldDirty: true });
  };

  const handleAdd = (itemId: string) =>
    setSelectedGuests(
      itemId,
      guestDetails.map((_, index) => String(index))
    );
  const handleRemove = (itemId: string) => setSelectedGuests(itemId, []);

  const labels = useMemo(
    () => ({
      ...sharedLabels,
      title: t('package.spaExtrasTitle'),
      subtitle: t('package.extrasSubtitle'),
      progressLabel: t('aria.bookingProgress'),
    }),
    [sharedLabels, t]
  );

  return (
    <PackageExtrasPage
      items={items}
      onAdd={handleAdd}
      onRemove={handleRemove}
      onConfirmGuestSelection={setSelectedGuests}
      labels={labels}
    />
  );
};
